import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import TrustBadges from '../components/ConversionElements';
import { generateWebPageSchema, generateFAQSchema, generateBreadcrumbSchema } from '../utils/schema';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SERVICE_CARDS = [
  { name: 'Rodent Control', slug: 'rodent-control', desc: 'Rat & mouse extermination, inspection, exclusion, and prevention for homes, apartments, and commercial properties.', icon: '🐀' },
  { name: 'Cockroach Extermination', slug: 'cockroach-extermination', desc: 'Roach control for apartments, homes, restaurants, and multi-unit buildings. German, American, and Oriental roach treatments.', icon: '🪳' },
  { name: 'Bed Bug Treatment', slug: 'bed-bug-removal', desc: 'Bed bug inspection, treatment, and follow-up for apartments, homes, and hotels. Discreet and effective.', icon: '🛏️' },
  { name: 'Ant Extermination', slug: 'ant-extermination', desc: 'Carpenter, pavement, pharaoh, and sugar ant removal. Targeted bait and perimeter treatments.', icon: '🐜' },
  { name: 'Wasp & Bee Removal', slug: 'wasp-removal', desc: 'Safe wasp, hornet, and bee nest removal from yards, walls, eaves, and attics.', icon: '🐝' },
  { name: 'Flea Control', slug: 'flea-control', desc: 'Pet-safe flea elimination for homes with cats and dogs. Multi-stage treatment.', icon: '🪲' },
  { name: 'Termite Control', slug: 'termite-control', desc: 'Termite inspections, liquid treatments, and bait systems to protect your property.', icon: '🪵' },
  { name: 'General Pest Control', slug: 'general-pest-control', desc: 'Quarterly and one-time pest control for spiders, silverfish, moths, flies, and more.', icon: '🛡️' }
];

const SERVICE_AREAS = [
  { name: 'Queens', slug: 'queens-ny' },
  { name: 'Brooklyn', slug: 'brooklyn-ny' },
  { name: 'Manhattan', slug: 'manhattan-ny' },
  { name: 'Bronx', slug: 'bronx-ny' },
  { name: 'Staten Island', slug: 'staten-island-ny' },
  { name: 'Nassau County', slug: 'nassau-county-ny' },
  { name: 'Suffolk County', slug: 'suffolk-county-ny' },
  { name: 'Westchester County', slug: 'westchester-ny' }
];

const PROCESS_STEPS = [
  { num: 1, title: 'Inspection', desc: 'We inspect your home or business to identify pests, entry points, nesting sites, and contributing conditions.' },
  { num: 2, title: 'Treatment Plan', desc: 'You receive a clear, customized treatment plan with pricing — no surprise fees or guesswork.' },
  { num: 3, title: 'Targeted Extermination', desc: 'Our licensed technicians apply targeted, family- and pet-safe treatments designed for your specific pest issue.' },
  { num: 4, title: 'Prevention & Follow-Up', desc: 'We seal entry points, share prevention tips, and schedule follow-up visits to keep your property pest-free.' }
];

const FAQS = [
  { question: 'How fast can you come?', answer: 'Reclaim Pest Control offers same-day service across most of NYC and Long Island. Call 646-209-9955 to schedule an inspection, often within a few hours.' },
  { question: 'Do you treat apartments?', answer: 'Yes. We treat apartments, condos, co-ops, and multi-unit buildings throughout NYC. We work with tenants, landlords, and property managers and follow building protocols.' },
  { question: 'Do you handle roaches, rats, mice, and bed bugs?', answer: 'Yes. Roaches, rats, mice, and bed bugs are among our most common treatments. We use targeted methods proven for NYC apartments, homes, and commercial properties.' },
  { question: 'Do you provide commercial pest control?', answer: 'Yes. Reclaim Pest Control serves restaurants, food establishments, offices, retail spaces, warehouses, and property management companies with commercial pest control programs.' },
  { question: 'Do you serve all NYC boroughs?', answer: 'Yes. We service Queens, Brooklyn, Manhattan, Bronx, and Staten Island, plus Nassau County, Suffolk County, and Westchester County.' }
];

const Home = () => {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [leadForm, setLeadForm] = useState({ name: '', phone: '', email: '', service_type: '', message: '' });
  const [leadStatus, setLeadStatus] = useState({ type: '', message: '' });
  const [leadLoading, setLeadLoading] = useState(false);

  const handleLeadChange = (e) => {
    setLeadForm({ ...leadForm, [e.target.name]: e.target.value });
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadLoading(true);
    setLeadStatus({ type: '', message: '' });
    try {
      const [firstName, ...lastParts] = leadForm.name.trim().split(' ');
      const payload = {
        first_name: firstName || leadForm.name,
        last_name: lastParts.join(' ') || '-',
        email: leadForm.email,
        phone: leadForm.phone,
        service_type: leadForm.service_type,
        message: leadForm.message || 'Requested pest inspection from homepage hero form.',
        location: '',
        preferred_contact: 'phone'
      };
      const res = await axios.post(`${API_URL}/api/contact`, payload);
      setLeadStatus({ type: 'success', message: res.data.message || 'Thanks! We will call you shortly.' });
      setLeadForm({ name: '', phone: '', email: '', service_type: '', message: '' });
    } catch (err) {
      setLeadStatus({ type: 'error', message: 'Something went wrong. Please call 646-209-9955 directly.' });
    } finally {
      setLeadLoading(false);
    }
  };

  const schemas = [
    generateWebPageSchema(
      'NYC Pest Control & Exterminator Services | Reclaim Pest Control',
      'Reclaim Pest Control Inc provides fast, professional pest control for roaches, rats, mice, bed bugs, ants, fleas, wasps, termites, and general pest problems throughout New York City and Long Island.',
      'https://www.reclaimpestcontrol.com'
    ),
    generateFAQSchema(FAQS),
    generateBreadcrumbSchema([
      { name: 'Home', url: '/' }
    ])
  ];

  return (
    <div>
      <SEO
        title="NYC Pest Control & Exterminator Services"
        description="Reclaim Pest Control Inc provides fast, professional pest control for roaches, rats, mice, bed bugs, ants, fleas, wasps, termites, and general pest problems throughout New York City and Long Island. Call 646-209-9955."
        keywords="pest control NYC, exterminator NYC, rodent control, rat exterminator, mouse exterminator, cockroach exterminator, bed bug exterminator, ant exterminator, flea control, wasp removal, termite control, Queens, Brooklyn, Manhattan, Bronx, Staten Island, Nassau County, Suffolk County, Westchester"
        schema={schemas}
        canonical="https://www.reclaimpestcontrol.com/"
      />

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center py-16 md:py-24"
        style={{ backgroundImage: 'url(/images/hero-technician.webp)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-green-900/85"></div>
        <div className="relative container mx-auto px-4">
          <div className="grid lg:grid-cols-5 gap-10 items-start">
            {/* Left: Headline + CTAs */}
            <div className="lg:col-span-3 text-white">
              <div className="inline-flex items-center bg-yellow-500/95 text-gray-900 px-3 py-1 rounded-full text-xs md:text-sm font-semibold mb-5">
                <span className="mr-2">★★★★★</span> 5-Star Rated NYC Exterminator
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-5 leading-tight drop-shadow-lg" data-testid="hero-h1">
                NYC Pest Control &amp; Exterminator Services
              </h1>
              <p className="text-base md:text-lg mb-6 max-w-2xl text-white/95">
                Reclaim Pest Control Inc provides fast, professional pest control for roaches, rats, mice, bed bugs, ants, fleas, wasps, termites, and general pest problems throughout New York City and Long Island.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <a
                  href="tel:6462099955"
                  className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-7 py-4 rounded-lg font-bold text-base md:text-lg shadow-lg transition flex items-center justify-center"
                  data-testid="hero-call-btn"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20"><path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" /></svg>
                  Call Now: 646-209-9955
                </a>
                <Link
                  to="/contact"
                  className="bg-white hover:bg-gray-100 text-green-700 px-7 py-4 rounded-lg font-bold text-base md:text-lg shadow-lg transition flex items-center justify-center border-2 border-white"
                  data-testid="hero-inspection-btn"
                >
                  Request a Pest Inspection
                </Link>
              </div>
              <p className="text-sm md:text-base text-white/90">
                Licensed &amp; Insured | Residential &amp; Commercial | Same-Day Service Available | NYC &amp; Long Island
              </p>
            </div>

            {/* Right: Quick Lead Form */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-2xl p-6 md:p-7" data-testid="hero-lead-form">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">Get a Free Pest Inspection</h2>
              <p className="text-sm text-gray-600 mb-4">Reply within minutes. No obligation.</p>
              <form onSubmit={handleLeadSubmit} className="space-y-3">
                <input
                  type="text"
                  name="name"
                  required
                  value={leadForm.name}
                  onChange={handleLeadChange}
                  placeholder="Your Name *"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent text-gray-900"
                  data-testid="hero-form-name"
                />
                <input
                  type="tel"
                  name="phone"
                  required
                  value={leadForm.phone}
                  onChange={handleLeadChange}
                  placeholder="Phone Number *"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent text-gray-900"
                  data-testid="hero-form-phone"
                />
                <input
                  type="email"
                  name="email"
                  required
                  value={leadForm.email}
                  onChange={handleLeadChange}
                  placeholder="Email *"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent text-gray-900"
                  data-testid="hero-form-email"
                />
                <select
                  name="service_type"
                  value={leadForm.service_type}
                  onChange={handleLeadChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent text-gray-900"
                  data-testid="hero-form-service"
                >
                  <option value="">What pest issue are you facing?</option>
                  <option value="Rodent Control">Rats / Mice</option>
                  <option value="Cockroach Extermination">Cockroaches</option>
                  <option value="Bed Bug Removal">Bed Bugs</option>
                  <option value="Ant Extermination">Ants</option>
                  <option value="Wasp Removal">Wasps / Bees</option>
                  <option value="Flea Control">Fleas</option>
                  <option value="Termite Control">Termites</option>
                  <option value="General Pest Control">General Pest Control</option>
                </select>
                {leadStatus.message && (
                  <div className={`p-3 rounded-lg text-sm ${leadStatus.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                    {leadStatus.message}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={leadLoading}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-lg transition disabled:bg-gray-400"
                  data-testid="hero-form-submit"
                >
                  {leadLoading ? 'Sending...' : 'Request Free Inspection'}
                </button>
                <p className="text-xs text-gray-500 text-center">
                  Or call <a href="tel:6462099955" className="text-green-700 font-semibold">646-209-9955</a> for immediate service.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <TrustBadges />

      {/* Service Cards */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Pest Control Services</h2>
            <p className="text-gray-600 text-base md:text-lg">
              Reclaim Pest Control handles every common NYC pest. Each service is performed by a licensed technician and tailored to your property.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="service-cards-grid">
            {SERVICE_CARDS.map((service) => (
              <Link
                key={service.slug}
                to={`/services/${service.slug}`}
                className="group bg-white border border-gray-200 rounded-xl p-5 hover:border-green-500 hover:shadow-xl transition flex flex-col"
                data-testid={`service-card-${service.slug}`}
              >
                <div className="text-4xl mb-3" aria-hidden="true">{service.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-green-600 transition">{service.name}</h3>
                <p className="text-sm text-gray-600 mb-4 flex-1">{service.desc}</p>
                <span className="text-green-600 font-semibold text-sm group-hover:underline">Learn more →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Service Areas</h2>
            <p className="text-gray-600 text-base md:text-lg">
              We provide pest control across all five NYC boroughs and Long Island. Click your area for local pest info.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto" data-testid="service-areas-grid">
            {SERVICE_AREAS.map((area) => (
              <Link
                key={area.slug}
                to={`/service-areas/${area.slug}`}
                className="bg-white border border-gray-200 rounded-lg px-4 py-5 text-center font-semibold text-gray-800 hover:border-green-500 hover:bg-green-50 hover:text-green-700 transition shadow-sm"
                data-testid={`service-area-link-${area.slug}`}
              >
                {area.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Pest Control Process</h2>
            <p className="text-gray-600 text-base md:text-lg">
              A clear, four-step process designed to solve your pest issue and keep it from coming back.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="process-grid">
            {PROCESS_STEPS.map((step) => (
              <div key={step.num} className="bg-gradient-to-br from-blue-50 to-green-50 border border-gray-100 rounded-xl p-6 relative">
                <div className="absolute -top-4 left-6 bg-green-600 text-white w-9 h-9 rounded-full flex items-center justify-center font-bold shadow-md">
                  {step.num}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 mt-3">{step.title}</h3>
                <p className="text-sm text-gray-700">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / Property Types Section */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Trusted Across NYC &amp; Long Island</h2>
            <p className="text-white/90 text-base md:text-lg">
              Professional pest control service for apartments, homes, restaurants, landlords, property managers, and commercial properties.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 max-w-5xl mx-auto">
            {['Apartments', 'Homes', 'Restaurants', 'Landlords', 'Property Managers', 'Commercial'].map((type) => (
              <div key={type} className="bg-white/10 border border-white/20 rounded-lg py-4 text-center font-semibold text-sm md:text-base">
                {type}
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <a
              href="tel:6462099955"
              className="inline-flex items-center bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-7 py-4 rounded-lg font-bold text-lg shadow-lg transition"
              data-testid="trust-call-btn"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20"><path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" /></svg>
              Call 646-209-9955
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-gray-600 text-base md:text-lg">Quick answers to common questions about our NYC pest control service.</p>
            </div>
            <div className="space-y-3" data-testid="faq-list">
              {FAQS.map((faq, idx) => (
                <div key={idx} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenFAQ(openFAQ === idx ? -1 : idx)}
                    className="w-full text-left px-5 py-4 flex items-center justify-between font-semibold text-gray-900 hover:bg-gray-50 transition"
                    data-testid={`faq-toggle-${idx}`}
                  >
                    <span>{faq.question}</span>
                    <span className={`text-green-600 text-xl transition-transform ${openFAQ === idx ? 'rotate-45' : ''}`}>+</span>
                  </button>
                  {openFAQ === idx && (
                    <div className="px-5 pb-5 text-gray-700 border-t border-gray-100" data-testid={`faq-answer-${idx}`}>
                      <p className="pt-3">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/faq" className="text-green-700 font-semibold hover:underline">
                See all FAQs →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Reclaim Your Space?</h2>
          <p className="text-lg md:text-xl mb-8 text-white/95 max-w-2xl mx-auto">
            Talk to a licensed NYC exterminator now. Same-day appointments available across NYC &amp; Long Island.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:6462099955"
              className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg shadow-lg transition"
              data-testid="footer-call-btn"
            >
              Call Now: 646-209-9955
            </a>
            <Link
              to="/contact"
              className="bg-white hover:bg-gray-100 text-green-700 px-8 py-4 rounded-lg font-bold text-lg shadow-lg transition border-2 border-white"
              data-testid="footer-quote-btn"
            >
              Request a Pest Inspection
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
